import {
  getModelIdFromColumn,
  getPropertyNameFromColumn,
} from "src/components/explore/visual/utils";
import { ChartVariant, type ParentModel } from "src/pages/analytics/types";
import { isGroupByColumnRelatedToParent } from "src/pages/analytics/utils";
import type { ColumnReference } from "src/types/visual";
import type { GraphSeries } from "src/components/charts/engine/types";
import type { GroupByColumn } from "@hightouch/lib/query/visual/types";
import { format } from "numerable";
import { useCallback, useMemo } from "react";

export const metricNameKey = "metricName";
export const audienceNameKey = "audienceName";
export const splitNameKey = "splitName";
export const valueKey = "value";
export const groupKey = "group";

export const STATIC_COLUMN_NAMES = [
  metricNameKey,
  audienceNameKey,
  splitNameKey,
  valueKey,
] as const;

export type GroupByColumnPath = `grouping.${number}.value`;

export type BreakdownColumn =
  | (typeof STATIC_COLUMN_NAMES)[number]
  | GroupByColumnPath;

export type BreakdownColumnKind =
  | (typeof STATIC_COLUMN_NAMES)[number]
  | typeof groupKey;

export type MoveableBreakdownColumn = Omit<BreakdownColumn, typeof valueKey>;

const sharedTdStyles = {
  textStyle: "mono",
  color: "text.secondary",
};

export const getTdStyles = ({
  isFirstRow,
  isLastColumn,
  isFirstOfGroup,
  isLastOfGroup,
  variant,
}: {
  isFirstRow: boolean;
  isLastColumn: boolean;
  isFirstOfGroup: boolean;
  isLastOfGroup: boolean;
  variant: ChartVariant;
}) => {
  if (isLastColumn) {
    return {
      ...sharedTdStyles,
      display: "flex",
      alignItems: "center",
      pr: variant == ChartVariant.Embedded ? 0 : 4,
      mx: variant == ChartVariant.Embedded ? 2 : 0,
      ":first-of-type": {
        ml: 0,
      },
    };
  }

  return {
    ...sharedTdStyles,
    bg: "base.background",
    px: 4,
    mx: variant == ChartVariant.Embedded ? 2 : 1,
    ":first-of-type": {
      ml: 0,
    },
    mr: 4, // Add margin here instead of on separate column so they aren't impacted if this is not present.
    display: "flex",
    alignItems: "center",
    minHeight: "32px",
    span: {
      visibility: "hidden",
    },
    ...(isFirstOfGroup && {
      borderTopRadius: "lg",
      mt: !isFirstRow ? 1 : 0,
      span: { visibility: "visible" },
    }),
    ...(isLastOfGroup && { borderBottomRadius: "lg" }),
  };
};

export const move = (
  array: MoveableBreakdownColumn[],
  columnName: MoveableBreakdownColumn,
  direction: "left" | "right",
): MoveableBreakdownColumn[] => {
  const index = array.findIndex((name) => name === columnName);
  const newArray = [...array];
  const item = newArray.splice(index, 1)[0];

  if (item) {
    if (direction === "left") {
      newArray.splice(index - 1, 0, item);
    } else if (direction === "right") {
      newArray.splice(index + 1, 0, item);
    }
    return newArray;
  }

  return array;
};

// Include model id to avoid name collisions between parent and event groupBy columns
export const getGroupingKey = (
  column: ColumnReference,
  parent: ParentModel | null,
) => {
  const columnModelId = getModelIdFromColumn(column);
  const columnName = getPropertyNameFromColumn(column) ?? "--";

  return isGroupByColumnRelatedToParent(parent, column as GroupByColumn)
    ? `${columnModelId}-${columnName}`
    : columnName;
};

/**
 * The max breakdown value will serve as a reference for the error bar's length
 * We want to account for the upperBound of the error bar metadata when showing error bars
 */
export const getMaxBreakdownValue = (
  data: GraphSeries[],
  showErrorBars: boolean,
) => {
  let maxValue = 0;

  data.forEach(({ data }) => {
    const dataPoint = data?.[0];

    // If showing error bars, use the upper confidence bound as reference if it exists
    const value =
      showErrorBars && dataPoint?.errorBounds
        ? (dataPoint.errorBounds?.upperBound ?? 0)
        : (dataPoint?.metricValue ?? 0);

    maxValue = Math.max(value, maxValue);
  });

  return maxValue;
};

export const getMaxDecimalPlaces = (data: GraphSeries[]) => {
  let maxDecimalPlaces = 0;

  data.forEach(({ data }) => {
    const value = data?.[0]?.metricValue ?? 0;
    if (!Number.isFinite(value) || Number.isInteger(value)) {
      return;
    }

    const valueString = value.toString();
    const decimalIndex = valueString.indexOf(".");
    maxDecimalPlaces = Math.max(
      decimalIndex === -1 ? 0 : valueString.length - decimalIndex - 1,
      maxDecimalPlaces,
    );
  });

  return maxDecimalPlaces;
};

// Shared value formatting for breakdown-style bar rows. Every row uses the
// same decimal precision (pass the getMaxDecimalPlaces result) so label
// lengths — and therefore bar widths — stay comparable; values below 1 may
// show up to 4 decimals, larger values up to 2.
export const formatBreakdownValue = (
  value: number,
  maxDecimalPlaces: number,
): string => {
  const maxAllowedDecimalPlaces = value < 1 ? 4 : 2;
  return format(
    value,
    "0,0." +
      (maxDecimalPlaces
        ? "0".repeat(Math.min(maxDecimalPlaces, maxAllowedDecimalPlaces))
        : "X"), // X format will remove decimal point as necessary
  );
};

// Value column is always appended to the end of the array in the table so
// we don't need to include it in the columnsOrder array
export const getColumnKeys = (
  groupByColumnPaths: GroupByColumnPath[],
  hasSplits: boolean,
  numberOfUniqueAudiences: number,
  columnsOrder?: MoveableBreakdownColumn[],
): MoveableBreakdownColumn[] => {
  if (columnsOrder?.length) {
    return columnsOrder;
  }

  const keys: MoveableBreakdownColumn[] = [];

  keys.push(metricNameKey);

  if (numberOfUniqueAudiences > 1) {
    keys.push(audienceNameKey);
  }

  if (hasSplits) {
    keys.push(splitNameKey);
  }

  if (groupByColumnPaths.length > 0) {
    keys.push(...groupByColumnPaths);
  }

  return keys;
};

export const getGroupingColumnPath = (index: number): GroupByColumnPath => {
  return `grouping.${index}.value`;
};

const formatCqw = (
  width: number,
  containerWidth: number,
  fixedDifference: number = 0,
) =>
  fixedDifference === 0
    ? `${(width / containerWidth) * 100}cqw`
    : `calc(${(width / containerWidth) * 100}cqw + ${fixedDifference}px)`;

export const useColumnSizer = (
  variant: ChartVariant,
  width: number | undefined,
) => {
  // Design (https://www.figma.com/design/R8Bks7FvrsCixkRR9amHCn/Dashboards?node-id=1153-540104&m=dev)
  // references 496px as the first breakpoint; this includes the padding applied at a level above
  // <BreakdownTable>
  const numEmbeddedDisplayColumns = useMemo(() => {
    if (!width || width < 464) {
      return 2;
    } else if (width < 600) {
      return 3;
    } else if (width < 736) {
      return 4;
    } else if (width < 872) {
      return 5;
    } else if (width < 1008) {
      return 6;
    } else {
      return 7;
    }
  }, [width]);

  return useCallback(
    ({
      kind,
      isLastColumn,
    }: {
      kind: BreakdownColumnKind;
      isLastColumn: boolean;
    }) => {
      if (variant == ChartVariant.Full) {
        switch (kind) {
          case metricNameKey:
            return { min: "240px", max: "240px" };
          case splitNameKey:
            return { min: "168px", max: "168px" };
          case audienceNameKey:
            return { min: "240px", max: "240px" };
          case groupKey:
            return { min: "240px" };
          case valueKey:
            return { min: "168px" };
          default:
            throw new Error(`Unknown kind: ${kind satisfies never}`);
        }
      }

      // This system using container-query-width units is an attempt to do two things:
      //   a) Design to a responsive grid calculated in pixels: https://www.figma.com/design/R8Bks7FvrsCixkRR9amHCn/Dashboards?node-id=1153-540104&m=dev
      //   b) Allow smooth resizing of very large bar charts by having the browser do the math.
      //
      // The major downside is that container query widths include the scrollbar width. We
      // compensate for this by taking 17px (a cross-platform guesstimate maximum) out of the bar
      // width.
      if (numEmbeddedDisplayColumns == 2) {
        // Text minimum width + 8px padding = 72px
        // last column and value : 72/152px or 72/135px in 224px
        if (isLastColumn) {
          return { min: formatCqw(72, 224), max: formatCqw(72, 224) };
        }

        if (kind == valueKey) {
          // Attempt to format correctly whether the scrollbar (of up to 17px) is visible or not
          return { min: formatCqw(152, 224, -17), max: formatCqw(152, 224) };
        }

        return { min: "50cqw", max: "50cqw" };
      } else {
        const minWidthForColumns = 464 + (numEmbeddedDisplayColumns - 3) * 136;

        if (kind == valueKey) {
          // Minimum bar width from design: 192px - a 17px buffer for the scrollbar
          return { min: formatCqw(192, minWidthForColumns, -17) };
        }

        // Column width: 128px + 8px padding
        return {
          min: formatCqw(136, minWidthForColumns),
          max: formatCqw(136, minWidthForColumns),
        };
      }
    },
    [variant, numEmbeddedDisplayColumns],
  );
};
